import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { Dayjs } from 'dayjs'
import { AdapterOptions } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { extractPeriodStartAndEndTimesWithTimezone } from 'components/Assessments/helpers'
import { Weekdays } from 'utils/constants'
import { CustomScheduleVisit } from 'components/Assessments/components/VisitScheduling/types'
import { groupVisitsByWeekday } from 'components/Assessments/components/VisitScheduling/utils'
import { SocAssessmentQuestionnaireItem } from '../types/questionnaire'
import { VisitSchedulingFormField } from '../sections/VisitScheduling/formField'
import { SocFormData } from '../types/SocFormData'
import { OtherAppointment } from '../sections/VisitScheduling/formData'

export const extractOtherAppointments = (
  flatAnswersArray: QuestionnaireResponseItem[],
  options?: AdapterOptions
): SocFormData[VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS] => {
  const otherAppointments = getAnswerValuesFromItems(
    flatAnswersArray,
    SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS
  )

  return otherAppointments.map((appointment) => {
    const appointmentItems = appointment.item ?? []

    const name =
      getAnswerValuesFromItems(
        appointmentItems,
        SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS_NAME
      )?.at(0) || undefined

    // Period is nested within a group item
    const periodItem =
      appointmentItems.find(
        (item) =>
          item.linkId ===
          SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS_PERIOD
      )?.item ?? []

    const period = extractPeriodStartAndEndTimesWithTimezone(
      periodItem,
      SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS_PERIOD_START,
      SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS_PERIOD_END,
      options?.caregiverTimezone
    )

    const repeatedDays =
      getAnswerValuesFromItems(
        appointmentItems,
        SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS_REPEATS_ON_DAY
      ) || []

    return { name, period, repeatedDays }
  })
}

export const extractCustomSchedule = (
  flatAnswersArray: QuestionnaireResponseItem[],
  options?: AdapterOptions
): SocFormData[VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS] => {
  const customScheduleVisits = getAnswerValuesFromItems(
    flatAnswersArray,
    SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS
  )
  return customScheduleVisits.map((visit) => {
    const visitItems = visit.item ?? []

    // Period is nested within a group item
    const periodItem =
      visitItems.find(
        (item) =>
          item.linkId ===
          SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_PERIOD
      )?.item ?? []

    const period = extractPeriodStartAndEndTimesWithTimezone(
      periodItem,
      SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_PERIOD_START,
      SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_PERIOD_END,
      options?.caregiverTimezone
    )

    const caregiverReference =
      getAnswerValuesFromItems(
        periodItem,
        SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_PERIOD_CAREGIVER
      )?.at(0) || null

    const weekday =
      getAnswerValuesFromItems(
        periodItem,
        SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_PERIOD_WEEKDAY
      )?.at(0) || null

    return {
      period: period as [Dayjs, Dayjs], // CustomScheduleVisit expects non-undefined period
      caregiverReference,
      weekday,
      serviceType: null
    }
  })
}

export const extractCustomScheduleByWeekday = (
  customSchedule: SocFormData[VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS]
): { weekday: Weekdays; visits: CustomScheduleVisit[] }[] => {
  const customScheduleByWeekday = groupVisitsByWeekday(customSchedule as CustomScheduleVisit[])
  return Object.entries(customScheduleByWeekday).map(([weekday, visits]) => {
    return {
      weekday: weekday as Weekdays,
      visits
    }
  })
}

export const convertOtherAppointmentsToFlatAnswers = (
  otherAppointments: SocFormData[VisitSchedulingFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS]
): unknown[] => {
  const appointmentItems =
    otherAppointments?.map((appointment: OtherAppointment) => {
      const periodItems: Array<{ linkId: string; value: unknown }> = []
      // Add period start and end if they exist
      periodItems.push({
        linkId:
          SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS_PERIOD_START,
        value: appointment?.period?.at(0)?.utc().toISOString()
      })

      periodItems.push({
        linkId:
          SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS_PERIOD_END,
        value: appointment?.period?.at(1)?.utc().toISOString()
      })

      const appointmentItemStructure = {
        item: [
          {
            linkId:
              SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS_NAME,
            answer: [{ valueString: appointment?.name }]
          },
          {
            linkId:
              SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS_PERIOD,
            item: periodItems.map((item) => ({
              linkId: item.linkId,
              answer: [{ valueString: item.value }]
            }))
          },
          {
            linkId:
              SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS_REPEATS_ON_DAY,
            answer: appointment?.repeatedDays?.map((day) => ({ valueString: day }))
          }
        ]
      }

      return appointmentItemStructure
    }) ?? []

  return appointmentItems
}

export const convertCustomScheduleToFlatAnswers = (
  customSchedule: SocFormData[VisitSchedulingFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS]
): unknown[] => {
  const visitItems =
    customSchedule?.map((visit) => {
      const periodItems: Array<{ linkId: string; value: unknown }> = []

      // Add period start and end if they exist
      if (visit.period?.at(0)) {
        periodItems.push({
          linkId:
            SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_PERIOD_START,
          value: visit.period.at(0)?.utc().toISOString()
        })
      }

      if (visit.period?.at(1)) {
        periodItems.push({
          linkId: SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_PERIOD_END,
          value: visit.period.at(1)?.utc().toISOString()
        })
      }

      // Add caregiver reference if it exists
      if (visit.caregiverReference) {
        periodItems.push({
          linkId:
            SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_PERIOD_CAREGIVER,
          value: visit.caregiverReference
        })
      }

      // Add weekday if it exists
      if (visit.weekday) {
        periodItems.push({
          linkId:
            SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_PERIOD_WEEKDAY,
          value: visit.weekday
        })
      }

      // Return the same structure as convertOtherAppointmentsToFlatAnswers
      const visitItemStructure = {
        item: [
          {
            linkId: SocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_PERIOD,
            item: periodItems.map((item) => ({
              linkId: item.linkId,
              answer: [{ valueString: item.value }]
            }))
          }
        ]
      }

      return visitItemStructure
    }) ?? []

  return visitItems
}
